import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Analytics() {
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation({ threshold: 0.2 });

  const stats = [
    { value: '10M+', label: 'Data Points Processed Daily' },
    { value: '99.9%', label: 'Uptime Guarantee' },
    { value: '&lt;100ms', label: 'Query Response Time' },
    { value: '500+', label: 'Enterprise Customers' }
  ];

  const handleDemoClick = () => {
    window.REACT_APP_NAVIGATE('/demo');
  };

  return (
    <section id="analytics" className="py-24 px-6 lg:px-16 bg-gray-950">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div
            ref={leftRef as React.RefObject<HTMLDivElement>}
            className={`transition-all duration-700 ${leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 rounded-full mb-6 border border-orange-500/30">
              <i className="ri-bar-chart-box-line text-orange-400"></i>
              <span className="text-sm font-semibold text-orange-400">ANALYTICS ENGINE</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Lightning-Fast Data Processing
            </h2>
            <p className="text-xl text-gray-400 mb-8 leading-relaxed">
              Our proprietary analytics engine processes millions of data points in seconds, delivering insights when you need them most. Built for scale, optimized for speed.
            </p>
            
            <div className="space-y-6 mb-10">
              {[
                { icon: 'ri-flashlight-line', color: 'teal', title: 'Instant Query Results', desc: 'Execute complex queries across billions of records in milliseconds with our distributed computing architecture.' },
                { icon: 'ri-refresh-line', color: 'cyan', title: 'Auto-Scaling Infrastructure', desc: 'Automatically scales to handle peak loads without manual intervention or performance degradation.' },
                { icon: 'ri-cpu-line', color: 'amber', title: 'Smart Caching', desc: 'Intelligent caching layer predicts and pre-loads frequently accessed data for instant retrieval.' },
              ].map((item, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-4 transition-all duration-600 ${leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                  style={{ transitionDelay: `${200 + i * 120}ms` }}
                >
                  <div className={`w-10 h-10 flex items-center justify-center bg-${item.color}-500/20 rounded-lg flex-shrink-0 border border-${item.color}-500/30`}>
                    <i className={`${item.icon} text-xl text-${item.color}-400`}></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-white mb-2">{item.title}</h4>
                    <p className="text-gray-400">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div
              ref={statsRef as React.RefObject<HTMLDivElement>}
              className="grid grid-cols-2 gap-6 mb-8"
            >
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className={`bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-xl border border-gray-700 transition-all duration-500 ${statsVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="text-3xl font-bold text-teal-400 mb-2" dangerouslySetInnerHTML={{ __html: stat.value }}></div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </div>
              ))}
            </div>

            <button 
              onClick={handleDemoClick}
              className="px-8 py-4 bg-teal-500 text-gray-900 rounded-lg font-semibold hover:bg-teal-400 transition-all cursor-pointer whitespace-nowrap shadow-xl"
            >
              Demo
            </button>
          </div>

          <div
            ref={rightRef as React.RefObject<HTMLDivElement>}
            className={`relative transition-all duration-700 delay-200 ${rightVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}
          >
            <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl border border-gray-800">
              <img 
                src="https://public.readdy.ai/ai/img_res/000dd13f03e143ec3ded6ebb4f5fa7c2.jpg"
                alt="Analytics Dashboard"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl opacity-20 blur-3xl"></div>
            <div className="absolute -top-6 -left-6 w-48 h-48 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-2xl opacity-20 blur-3xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
}
